import clsx from "clsx";
import type { ComponentPropsWithRef, ReactNode } from "react";
import { useControlSize, type ControlSize } from "./Button.js";

/**
 * Labelled form fields with hint and error wiring.
 *
 * Every control gets its label by `id`, its hint and error through
 * `aria-describedby`, and `aria-invalid` while an error is shown, so the
 * association survives no matter how the control is rendered. Required is the
 * default; optional fields say so visibly instead of decorating required ones
 * with asterisks — except the few that are optional without saying so
 * (`optional="untagged"`): the order form's VIN, RO and technician, which
 * the owner wants left plain (2026-09-17), still drop `aria-required`.
 */

/** Attributes a field hands to whatever control it wraps. */
export type FieldControlProps = Readonly<{
  id: string;
  "aria-describedby": string | undefined;
  "aria-invalid": true | undefined;
  "aria-required": true | undefined;
}>;

export interface FormFieldProps {
  id: string;
  label: ReactNode;
  hint?: ReactNode;
  /** Validation message shown next to the control; `null`/`undefined` means valid. */
  error?: string | null;
  /**
   * Marks the field as optional: `true` shows the "Optional" tag and omits
   * `aria-required`; `"untagged"` omits `aria-required` but shows no tag.
   */
  optional?: boolean | "untagged";
  className?: string;
  children: (control: FieldControlProps) => ReactNode;
}

export function FormField({
  id,
  label,
  hint,
  error,
  optional = false,
  className,
  children,
}: FormFieldProps) {
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(" ");

  const control: FieldControlProps = {
    id,
    "aria-describedby": describedBy || undefined,
    "aria-invalid": error ? true : undefined,
    "aria-required": optional ? undefined : true,
  };
  const tagged = optional === true;

  return (
    <div
      className={clsx("field", className)}
      data-invalid={error ? "true" : undefined}
    >
      <div className="field__labels">
        <label htmlFor={id} className="field__label">
          {label}
        </label>
        {tagged ? <span className="field__optional">Optional</span> : null}
      </div>
      {children(control)}
      {hint ? (
        <p id={hintId} className="field__hint">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="field__error">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface TextFieldProps extends Omit<
  ComponentPropsWithRef<"input">,
  "id" | "size" | "aria-describedby" | "aria-invalid"
> {
  id: string;
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  optional?: boolean | "untagged";
  size?: ControlSize;
  fieldClassName?: string;
}

/** A single-line text input with the full FormField wiring applied. */
export function TextField({
  id,
  label,
  hint,
  error,
  optional,
  size,
  fieldClassName,
  className,
  ...inputProps
}: TextFieldProps) {
  const resolvedSize = useControlSize(size);
  return (
    <FormField
      id={id}
      label={label}
      hint={hint}
      error={error}
      optional={optional}
      className={fieldClassName}
    >
      {(control) => (
        <input
          {...inputProps}
          {...control}
          className={clsx("text-input", className)}
          data-size={resolvedSize}
        />
      )}
    </FormField>
  );
}
